import { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function About() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-800 via-purple-800 to-indigo-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="space-y-12"
        >
          <motion.div variants={itemVariants} className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              About <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Trifusion Solutions</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full"></div>
          </motion.div>

          <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-white">
                Who We Are
              </h3>
              <p className="text-gray-200 leading-relaxed">
                Trifusion Solutions is a forward-thinking tech company dedicated to delivering innovative digital solutions that empower businesses to thrive 
                in a rapidly evolving digital landscape.
              </p>
              <p className="text-gray-200 leading-relaxed">
                With a deep-rooted passion for technology and a commitment to excellence, we bring years of development expertise to every project we undertake. 
                Our journey in software development has been defined by a love for problem-solving, continuous learning, and a drive to push the boundaries of what technology can achieve.
              </p>
              <p className="text-gray-200 leading-relaxed">
                Whether we're coding complex applications or brainstorming new digital strategies, we consistently strive to create solutions that are both impactful and forward-looking.
              </p>
            </div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 gap-6"
            >
              <div className="p-6 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl text-center shadow-lg border border-purple-400/20 hover:shadow-purple-400/20 transition-all duration-300 backdrop-blur-sm">
                <h4 className="text-4xl font-bold bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent mb-2">3+</h4>
                <p className="text-gray-200">Years Experience</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl text-center shadow-lg border border-purple-400/20 hover:shadow-purple-400/20 transition-all duration-300 backdrop-blur-sm">
                <h4 className="text-4xl font-bold bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent mb-2">50+</h4>
                <p className="text-gray-200">Projects Completed</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl text-center shadow-lg border border-purple-400/20 hover:shadow-purple-400/20 transition-all duration-300 backdrop-blur-sm">
                <h4 className="text-4xl font-bold bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent mb-2">20+</h4>
                <p className="text-gray-200">Happy Clients</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl text-center shadow-lg border border-purple-400/20 hover:shadow-purple-400/20 transition-all duration-300 backdrop-blur-sm">
                <h4 className="text-4xl font-bold bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent mb-2">100%</h4>
                <p className="text-gray-200">Client Satisfaction</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}